import { ConfirmDialog, isolate } from '@batiflow/ui';
import { Button, __css, tokens } from '@fluentui/react-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SettingsSection } from '@/features/admin/components/settings-section';
import { useNotify } from '@/shared/hooks/use-notify';
import { isStepUpRequired, type Space } from '../model';
import { useCloseSpace } from './api';
import { sharingErrorMessage } from './model';

const useStyles = __css({"note":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Be2twd7":"fkhj508","sj55zd":"fkfq4zb"},"list":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","B1hrpyx":"f12g30ep"},"actions":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","i8kkvl":"f1xcgk68","Belr9w4":"f1wn3y0l"}});

/**
 * "Fermer l'espace" (owners and administrators of the host): every outside access ends at once and
 * the space becomes read-only. Partners' export of their own files comes later (V1), and the
 * confirmation says so. The API asks for a recent identity check (step-up).
 */
export function CloseSpaceSection({ space }: { space: Space }) {
  const { t } = useTranslation('spaces');
  const { t: tCommon } = useTranslation('common');
  const styles = useStyles();
  const notify = useNotify();
  const close = useCloseSpace(space.id);
  const [confirming, setConfirming] = useState(false);

  const reportError = (error: unknown) => {
    // STEP_UP_REQUIRED: the app's step-up dialog is open now and replays the closing.
    if (isStepUpRequired(error)) return;
    notify({
      intent: 'error',
      message: sharingErrorMessage(t, tCommon, error, {
        forbidden: 'sharing.close.errors.forbidden',
      }),
    });
  };

  return (
    <>
      <SettingsSection
        title={t('sharing.close.title')}
        description={t('sharing.close.description')}
      >
        <div className={styles.actions}>
          <Button
            appearance="secondary"
            onClick={() => {
              setConfirming(true);
            }}
          >
            {t('sharing.close.button')}
          </Button>
          <p className={styles.note}>{t('sharing.close.stepUpNote')}</p>
        </div>
      </SettingsSection>
      <ConfirmDialog
        open={confirming}
        destructive
        title={t('sharing.close.confirmTitle', { name: isolate(space.name) })}
        body={
          <>
            <p className={styles.note}>{t('sharing.close.confirmIntro')}</p>
            <ul className={styles.list}>
              <li>{t('sharing.close.effects.access')}</li>
              <li>{t('sharing.close.effects.readOnly')}</li>
              <li>{t('sharing.close.effects.export')}</li>
            </ul>
          </>
        }
        confirmLabel={t('sharing.close.confirm')}
        onOpenChange={setConfirming}
        onConfirm={async () => {
          await close.mutateAsync({ spaceId: space.id });
          notify({ intent: 'success', message: t('sharing.close.done') });
        }}
        onError={reportError}
      />
    </>
  );
}


import "/opt/batiflow/app/apps/web/src/features/spaces/sharing/close-space-section.griffel.css";